import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { Header } from '../../../components/Header';
import { Footer } from '../../../components/Footer';
import { 
  Cpu, CheckCircle2, Circle, AlertTriangle, 
  AlertCircle, RefreshCw, ArrowLeft, Zap, 
  Terminal, Copy, Lock, ShieldCheck, ArrowRight
} from 'lucide-react';
import { useHistory } from '../../../context/HistoryContext';
import { ReportEngine } from '../../../services/reportEngine';
import { useDiagnosticContext } from '../../../context/DiagnosticContext';
import styles from '../../DiagnosticFlow.module.css';

/**
 * Legacy PowerShell-based manual diagnostic flow.
 * Now accessible at /diagnostic/advanced
 */
export function ManualDiagnostic() {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const { saveReport } = useHistory();
  const { 
    setSelectedMode, setSelectedProblem, setUploadedData, 
    setInputValid, setAnalysisResult, setAnalysisStatus, 
    analysisStatus, analysisResult, setCreditDeducted,
    selectedProblem, inputValid
  } = useDiagnosticContext();

  const [commandResults, setCommandResults] = useState<Record<number, string>>({});
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Sync command results
    const combined = Object.values(commandResults).join('\n\n--- COMMAND SEPARATOR ---\n\n');
    if (combined.trim().length > 30) {
      setUploadedData(combined, 'paste');
      setInputValid(true);
    }
  }, [commandResults]);

  const handleCommandResultChange = (index: number, val: string) => {
    setCommandResults(prev => ({ ...prev, [index]: val }));
  };

  const runAIAnalysis = async () => {
    setIsProcessing(true);
    setAnalysisStatus('running');
    // ... analysis logic ...
    // Simplified for legacy component
    navigate('/diagnostic/report'); 
  };

  const getProblemCommands = () => {
    return [
      {
        title: "1. System Information",
        cmd: "Get-ComputerInfo | Select-Object CsManufacturer,CsModel,WindowsProductName,WindowsVersion,OsArchitecture,CsTotalPhysicalMemory,OsLastBootUpTime",
        desc: "Collects core hardware and OS details.",
        trust: "Read-only"
      },
      {
        title: "2. Storage Health",
        cmd: 'Get-CimInstance Win32_LogicalDisk -Filter "DriveType=3" | Select-Object DeviceID, Size, FreeSpace',
        desc: "Analyses disk space.",
        trust: "Safe"
      }
    ];
  };

  return (
    <div className="app" ref={topRef}>
      <Header />
      <main className={styles.flowPage}>
        <div className="wrap">
          <div className={styles.stepContainer}>
            <header className={styles.stepHeader}>
              <button className={styles.backLink} onClick={() => navigate('/diagnostic')}>
                <ArrowLeft size={14} /> Back to standard diagnostic
              </button>
              <h1>Advanced Manual Diagnostic</h1>
              <p>Copy and paste PowerShell commands manually to collect data.</p>
            </header>

            <div className={styles.commandList}>
              {getProblemCommands().map((cmd, i) => (
                <div key={i} className={styles.commandItem}>
                  <h4>{cmd.title}</h4>
                  <div className={styles.cmdBox}>
                    <code>{cmd.cmd}</code>
                    <button className={styles.copyBtn} onClick={() => navigator.clipboard.writeText(cmd.cmd)}>
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                  <textarea 
                    className={styles.smallPasteArea}
                    placeholder="Paste result here..."
                    onChange={(e) => handleCommandResultChange(i, e.target.value)}
                  />
                </div>
              ))}
            </div>

            <footer className={styles.actions} style={{ marginTop: '40px' }}>
              <button 
                className={styles.nextBtn} 
                disabled={Object.keys(commandResults).length === 0}
                onClick={() => navigate('/diagnostic/upload')}
              >
                Review Results <ArrowRight size={18} />
              </button>
            </footer>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
